import { Icon } from '@assembly-js/design-system';
import { useTranslation } from 'react-i18next';
import {
  FiEdit as EditIcon,
  FiExternalLink as OpenPortalIcon,
  FiNavigation as NavigationIcon,
} from 'react-icons/fi';

import { COACHMARK_HIGHLIGHT_CLASS } from 'src/components/Coachmark';
import { DetailsIcon, TrashIcon } from 'src/components/icons';
import { useFeatureFlags } from 'src/context';
// eslint-disable-next-line no-restricted-imports
import { Action, BaseActionsMenuButton } from 'src/legacy/components/Dropdowns';

import { ClientActionsRendererProps } from './types';

export const ClientActionRenderer = ({
  data,
  handleClickRemove,
  handleClickEdit,
  handleClickInvite,
  handleClickCopyAccessLink,
  handleClickEmailAccessLink,
  handleClickViewDetails,
  onActionMenuClick,
  node,
  isCoachmarkActive = false,
  onOpenPortalCoachmark,
}: ClientActionsRendererProps) => {
  const { t } = useTranslation();

  const { EnableStudioFTUX } = useFeatureFlags();

  const actions: Action[] = [
    {
      name: 'Edit info',
      icon: <EditIcon />,
      onClick: () => handleClickEdit(data),
    },
  ];
  if (data.status !== 'Active' && handleClickInvite) {
    actions.push({
      name: `${
        data.status === 'Inactive' ? 'Send invite' : 'Invite client'
      }...`,
      onClick: () => handleClickInvite(data),
      icon: <NavigationIcon />,
    });
  }

  if (handleClickEmailAccessLink) {
    actions.push({
      name: `Email magic link`,
      onClick: () => handleClickEmailAccessLink(data),
      icon: <Icon icon="Invite" className="h-4 w-4 text-gray-small" />,
    });
  }
  if (handleClickCopyAccessLink) {
    actions.push({
      name: `Copy magic link`,
      onClick: () => handleClickCopyAccessLink(data),
      icon: <Icon icon="Copy" className="h-4 w-4 text-gray-small" />,
    });
  }

  if (handleClickViewDetails) {
    actions.unshift({
      name: 'View details',
      icon: <DetailsIcon />,
      onClick: () => handleClickViewDetails(data),
    });
  }
  actions.push({
    name: t(($) => $.actions.delete_client),
    onClick: () => handleClickRemove(data),
    icon: <TrashIcon />,
    isDelete: true,
  });

  // Coachmark presentation, only while the tour's menu step is active and only on
  // the first row (the one the tour opens): a lit "Open portal" item on top, every
  // real action dimmed. Clicking "Open portal" completes the tour.
  const menuActions: Action[] =
    EnableStudioFTUX && isCoachmarkActive && node?.rowIndex === 0
      ? [
          {
            name: t(($) => $.crm.coachmark.menu.open_portal),
            icon: <OpenPortalIcon />,
            onClick: () => {
              // TODO: open the client portal experience here, as the sidebar's
              // "Open portal" does via window.open(clientExperienceLink). Real
              // navigation is deferred to a follow-up; for now this only ends
              // the tour, so the "aha" step doesn't actually open the portal yet.
              onOpenPortalCoachmark?.();
            },
            classes: [COACHMARK_HIGHLIGHT_CLASS],
          },
          ...actions.map((action) => ({ ...action, isDisabled: true })),
        ]
      : actions;

  return (
    <BaseActionsMenuButton
      onActionMenuClick={onActionMenuClick}
      actions={menuActions}
    />
  );
};
